// src/utils/aiAnalysisUtils.ts - AI Analysis utilities and step validation
import { FormState } from '../types/bcpForm';

export interface AIAnalysisStrategy {
  trigger: 'field_based' | 'step_complete';
  minData: string;
  debounce: number;
  analysisType: 'basic_validation' | 'risk_assessment' | 'business_impact' | 'operations' | 'team_structure' | 'mitigation_strategies';
  planType: 'new' | 'existing' | 'both';
  requires?: string;
  fallback: 'local_insights' | 'template_based';
}

export interface RiskState {
  primary_concerns: string[];
  location_risks: string[];
}

/**
 * Check if step 1 is complete with sufficient data for AI analysis
 */
export const isStep1Complete = (formData: FormState): boolean => {
  return !!(
    formData.business_name &&
    formData.business_name.length > 2 &&
    formData.business_type &&
    formData.industry &&
    formData.employee_count > 0 &&
    formData.primary_location.name &&
    formData.primary_location.address &&
    formData.primary_location.state &&
    formData.primary_location.zip
  );
};

/**
 * Check if step 2 is complete with risk data
 */
export const isStep2Complete = (risks?: RiskState): boolean => {
  return !!(
    risks &&
    risks.primary_concerns &&
    risks.primary_concerns.length > 0 &&
    risks.location_risks &&
    risks.location_risks.length > 0
  );
};

/**
 * Check if step 3 has critical functions
 */
export const hasCriticalFunctions = (formData: FormState): boolean => {
  return !!(formData.critical_functions && formData.critical_functions.length > 0);
};

/**
 * Check if step 4 has operations data
 */
export const hasOperations = (formData: FormState): boolean => {
  return !!(
    formData.it_systems &&
    formData.it_systems.length > 0 &&
    formData.key_suppliers &&
    formData.key_suppliers.length > 0
  );
};

/**
 * Check if step 5 has team data
 */
export const hasTeamData = (employees?: any[]): boolean => {
  return !!(employees && employees.length > 0);
};

/**
 * Get AI analysis strategy based on step and plan type
 */
export const getAnalysisStrategy = (
  step: number,
  formData: FormState,
  bcpId?: string,
  risks?: RiskState,
  employees?: any[]
): AIAnalysisStrategy => {
  const isNewPlan = !bcpId;

  switch (step) {
    case 1:
      return {
        trigger: 'field_based',
        minData: 'business_name > 2 chars OR business_type OR industry',
        debounce: 1000,
        analysisType: 'basic_validation',
        planType: 'both',
        fallback: 'local_insights'
      };

    case 2:
      return {
        trigger: 'step_complete',
        minData: 'complete_step_1',
        debounce: 1500,
        analysisType: 'risk_assessment',
        planType: isNewPlan ? 'new' : 'existing',
        requires: 'complete_step_1',
        fallback: 'template_based'
      };

    case 3:
      return {
        trigger: 'step_complete',
        minData: 'complete_step_1_and_2',
        debounce: 2000,
        analysisType: 'business_impact',
        planType: isNewPlan ? 'new' : 'existing',
        requires: 'complete_step_1_and_2',
        fallback: 'template_based'
      };

    case 4:
      return {
        trigger: 'step_complete',
        minData: 'complete_step_1_and_2_and_3',
        debounce: 2000,
        analysisType: 'operations',
        planType: isNewPlan ? 'new' : 'existing',
        requires: 'complete_step_1_and_2_and_3',
        fallback: 'template_based'
      };

    case 5:
      return {
        trigger: 'step_complete',
        minData: 'complete_step_1_and_2_and_3_and_4',
        debounce: 2000,
        analysisType: 'mitigation_strategies',
        planType: isNewPlan ? 'new' : 'existing',
        requires: 'complete_step_1_and_2_and_3_and_4',
        fallback: 'template_based'
      };

    default:
      return {
        trigger: 'step_complete',
        minData: 'complete_all_steps',
        debounce: 1500,
        analysisType: 'basic_validation',
        planType: 'both',
        fallback: 'local_insights'
      };
  }
};

/**
 * Check if AI should trigger based on strategy and current data
 */
export const shouldTriggerAI = (
  strategy: AIAnalysisStrategy,
  formData: FormState,
  risks?: RiskState,
  employees?: any[]
): boolean => {
  if (strategy.trigger === 'field_based') {
    // For step 1, check if we have minimal data
    return (
      (formData.business_name && formData.business_name.length > 2) ||
      !!formData.business_type ||
      !!formData.industry ||
      !!formData.employee_count
    );
  }

  if (strategy.trigger === 'step_complete') {
    // Check requirements based on strategy
    if (strategy.requires === 'complete_step_1') {
      return isStep1Complete(formData);
    }

    if (strategy.requires === 'complete_step_1_and_2') {
      return isStep1Complete(formData) && isStep2Complete(risks);
    }

    if (strategy.requires === 'complete_step_1_and_2_and_3') {
      return isStep1Complete(formData) && isStep2Complete(risks) && hasCriticalFunctions(formData);
    }

    if (strategy.requires === 'complete_step_1_and_2_and_3_and_4') {
      return (
        isStep1Complete(formData) &&
        isStep2Complete(risks) &&
        hasCriticalFunctions(formData) &&
        hasOperations(formData)
      );
    }

    if (strategy.requires === 'complete_all_steps') {
      return (
        isStep1Complete(formData) &&
        isStep2Complete(risks) &&
        hasCriticalFunctions(formData) &&
        hasOperations(formData) &&
        hasTeamData(employees)
      );
    }
  }

  return false;
};

/**
 * Get insight icon configuration
 */
export const getInsightIconConfig = (type: string) => {
  switch (type) {
    case 'risk':
      return {
        className: 'w-4 h-4 text-red-500',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24',
        path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
      };
    case 'suggestion':
      return {
        className: 'w-4 h-4 text-blue-500',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24',
        path: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
      };
    case 'warning':
      return {
        className: 'w-4 h-4 text-orange-500',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24',
        path: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      };
    case 'info':
      return {
        className: 'w-4 h-4 text-gray-500',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24',
        path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      };
    default:
      return {
        className: 'w-4 h-4 text-gray-500',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24',
        path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      };
  }
};

/**
 * Get priority color
 */
export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'text-red-600';
    case 'medium':
      return 'text-yellow-600';
    case 'low':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Get completion score color
 */
export const getCompletionScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

/**
 * Get completion score background color
 */
export const getCompletionScoreBgColor = (score: number) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};

/**
 * Sort insights by priority
 */
export const sortInsightsByPriority = (insights: any[]) => {
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  return insights.sort((a, b) => {
    const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 3;
    const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 3;
    return aPriority - bPriority;
  });
};